import { Injectable }               from '@angular/core';
import { RequestCompleteListState } from '@core/interfaces/api.interface';
import { StoreActionsEnum }         from '@core/interfaces/store.interface';
import { AuthorizationService }     from '@core/services/authorization/authorization.service';
import { ApplicationScopeTypes }    from '@models/scope.enums';
import {
	select,
	Store
}                                   from '@ngrx/store';
import {
	FilterType,
	IActiveFilters,
	IFilter
}                                   from '@shared/list-operations/model/filter.model';
import {
	IListOperationsConfig,
	IListOperationsHttpParams,
	IRestoreListOperationsData,
	ListOperations,
	ListOperationsIdentifierEnum,
	ListOperationsResetModeEnum,
	ListOperationsResetType
}                                   from '@shared/list-operations/model/list-operations.model';
import { IPaging }                  from '@shared/list-operations/model/paging.model';
import { IListOperationsActions }   from '@shared/list-operations/store/list-operations.actions';
import { listOperationsStore }      from '@shared/list-operations/store/list-operations.store';
import { ManagedObject }            from '@shared/models/managed-object.model';
import { CurrentUserService }       from '@shared/services/current-user.service';
import { LoggerService }            from '@shared/services/logger.service';
import { UiService }                from '@shared/services/ui.service';
import {
	ArrayUtils,
	ObjectUtils
}                                   from '@utils/helpers';
import {
	Observable,
	OperatorFunction
}                                   from 'rxjs';
import {
	distinctUntilChanged,
	skipWhile,
	switchMap,
	take,
	tap
}                                   from 'rxjs/operators';

@Injectable( {
	providedIn: 'root'
} )
export class ListOperationsService {

	private readonly listOperationsSelectors = listOperationsStore.selectors;
	private readonly listOperationsActions: IListOperationsActions = listOperationsStore.actions;

	constructor(
		private readonly authorizationService: AuthorizationService,
		private readonly currentUserService: CurrentUserService,
		private readonly logger: LoggerService,
		private readonly store$: Store,
		private readonly uiService: UiService
	) {}

	get select() {
		return {
			activeFilters: this.activeFilters,
			allListOperations: this.allListOperations,
			activeListOperationsId: this.activeListOperationsId,
			configData: this.configData,
			filterIsInitialized: this.filterIsInitialized,
			hideFilter: this.hideFilter,
			hideSearch: this.hideSearch,
			hideSort: this.hideSort,
			listOperationsById: this.listOperationsById,
			paging: this.paging,
			pagingIsInitialized: this.pagingIsInitialized,
			resetListOperationsData: this.resetListOperationsData,
			restoreListOperationsData: this.restoreListOperationsData,
			renderFilter: this.renderFilter,
			renderSearch: this.renderSearch,
			renderSort: this.renderSort,
			search: this.search,
			searchIsInitialized: this.searchIsInitialized,
			sort: this.sort,
			sortIsInitialized: this.sortIsInitialized
		};
	}

	get state() {
		return {
			activeFiltersSet: this.setActiveFilters,
			activeListOperationsIdSet: this.setActiveListOperationsId,
			filterIsInitializedSet: this.setFilterIsInitialized,
			hideFilterSet: this.setHideFilter,
			hideSearchSet: this.setHideSearch,
			hideSortSet: this.setHideSort,
			listOperationsClearAndKeepRestoreData: this.clearListOperationsButKeepRestoreData,
			listOperationsRemove: this.removeListOperations,
			listOperationsUpsert: this.upsertListOperations,
			listOperationsSetRestoreFlag: this.setListOperationsRestoreFlag,
			pagingReset: this.resetPaging,
			pagingSet: this.setPaging,
			pagingIsInitializedSet: this.setPagingIsInitialized,
			renderFilterSet: this.setRenderFilter,
			renderSearchSet: this.setRenderSearch,
			renderSortSet: this.setRenderSort,
			resetListOperationsDataSet: this.setResetListOperationsData,
			restoreListOperationsDataSet: this.setRestoreListOperationsData,
			searchIsInitializedSet: this.setSearchIsInitialized,
			sortIsInitializedSet: this.setSortIsInitialized,
			searchSet: this.setSearch,
			sortSet: this.setSort
		};
	}

	get helper() {
		return {
			filterResetDataByResetMode: this.filterResetDataByResetMode.bind( this ),
			requestWithStoredListOperationsData: this.requestWithStoredListOperationsData,
			sanitizeListOpsConfigToUserPermissions: this.sanitizeListOpsConfigToUserPermissions
		};
	}

	// local helpers #########################
	private updateListOperationProperty(
		listOperationId: ListOperationsIdentifierEnum,
		callback: ( listOperation: ListOperations ) => ListOperations,
		storeAction: StoreActionsEnum.SET_ONE | StoreActionsEnum.UPSERT_ONE,
		caller: string
	): void {
		this.select.listOperationsById( listOperationId, caller )
		.pipe( take( 1 ) )
		.subscribe( ( listOperation: ListOperations ) => {
			listOperation = callback( listOperation );

			switch ( storeAction ) {
				case StoreActionsEnum.SET_ONE:
					this.setListOperations( listOperation, caller );
					break;
				case StoreActionsEnum.UPSERT_ONE:
					this.upsertListOperations( listOperation, caller );
					break;
			}
		} );
	}

	/**
	 * returns the value from store only if its changed and not undefined
	 * or undefined when value was removed from store
	 * @param selector pipeable select OperatorFunction with store selector => select( {{store selector}}( args ) )
	 * @param caller the class name and identifier of the calling method (for debugging purposes)
	 * @param ignoreUndefined pass 'true' to allow emission even though the selected (piece of) state does not exist (yet)
	 * @param disableDistinctUntilChanged pass 'true' to allow emission even though the selected (piece of) state has not changed since the last emission
	 */
	private selectOnExistingValueAndChangeOnly<T>(
		selector: OperatorFunction<object, T>,
		caller: string,
		ignoreUndefined?: boolean,
		disableDistinctUntilChanged?: boolean
	): Observable<T | undefined> {
		this.logger.debug( caller );

		return this.store$
		?.pipe(
			selector,
			skipWhile( ( result: T ) => result === undefined
																	? !ignoreUndefined
																	: false
			),
			distinctUntilChanged(
				( resultA: T, resultB: T ) =>
					disableDistinctUntilChanged
					? false
					: ObjectUtils.simpleIsEqual( resultA, resultB )
			)
		);
	}


	// ACTIONS #########################
	private setListOperations = ( listOperations: ListOperations, caller: string ): void => {
		this.store$?.dispatch( this.listOperationsActions.setOne( { entity: listOperations, caller } ) );
	};

	private upsertListOperations = ( listOperations: ListOperations, caller: string ): void => {
		this.store$?.dispatch( this.listOperationsActions.upsertOne( { entity: listOperations, caller } ) );
	};

	private removeListOperations = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): void => {
		this.logger.debug( `${ caller }` );
		this.store$?.dispatch( this.listOperationsActions.removeOne( { entityId: listOperationId, caller } ) );
	};

	private clearListOperationsButKeepRestoreData = (
		listOperationId: ListOperationsIdentifierEnum,
		caller: string
	): void => {
		this.logger.debug( `${ caller }` );
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return {
				id: listOperation?.id,
				configData: {
					restoreListOperationsData: {
						...listOperation?.configData?.restoreListOperationsData,
						shouldRestoreData: false
					}
				},
				restoreListOperationsData: listOperation?.restoreListOperationsData
			};
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.SET_ONE, caller );
	};

	private resetPaging = (
		listOperationId: ListOperationsIdentifierEnum, caller: string
	): void => {
		this.state.pagingSet(
			listOperationId,
			{ pageNumber: 0, pageSize: 20, resetPaging: true, blockOperationEmit: true },
			caller
		);
	};

	private setActiveFilters = ( listOperationId: ListOperationsIdentifierEnum, activeFilters: IActiveFilters, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, activeFilters };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setActiveListOperationsId = ( activeListOperationsId: ListOperationsIdentifierEnum, caller: string ): void => {
		this.store$?.dispatch( this.listOperationsActions.setActiveListOperationsId( { activeListOperationsId, caller } ) );
	};

	private setFilterIsInitialized = ( listOperationId: ListOperationsIdentifierEnum, filterIsInitialized: boolean, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, filterIsInitialized };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setHideFilter = ( listOperationId: ListOperationsIdentifierEnum, hideFilter: boolean, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, hideFilter };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setHideSearch = ( listOperationId: ListOperationsIdentifierEnum, hideSearch: boolean, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, hideSearch };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setHideSort = ( listOperationId: ListOperationsIdentifierEnum, hideSort: boolean, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, hideSort };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setListOperationsRestoreFlag = (
		listOperationId: ListOperationsIdentifierEnum,
		shouldRestoreData: boolean,
		caller: string
	): void => {
		this.select.restoreListOperationsData( listOperationId, caller )
		.pipe( take( 1 ) )
		.subscribe( ( restoreListOperationsData: IRestoreListOperationsData ) => {
			restoreListOperationsData = {
				...restoreListOperationsData,
				shouldRestoreData
			};
			this.setRestoreListOperationsData( listOperationId, restoreListOperationsData, caller );
		} );
	};

	private setPaging = ( listOperationId: ListOperationsIdentifierEnum, paging: IPaging, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, paging };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setPagingIsInitialized = ( listOperationId: ListOperationsIdentifierEnum, pagingIsInitialized: boolean, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, pagingIsInitialized };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setRenderFilter = ( listOperationId: ListOperationsIdentifierEnum, renderFilter: boolean, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, renderFilter };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setRenderSearch = ( listOperationId: ListOperationsIdentifierEnum, renderSearch: boolean, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, renderSearch };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setRenderSort = ( listOperationId: ListOperationsIdentifierEnum, renderSort: boolean, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, renderSort };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setRestoreListOperationsData = (
		listOperationId: ListOperationsIdentifierEnum, restoreListOperationsData: IRestoreListOperationsData, caller: string
	): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return {
				...listOperation,
				configData: {
					...listOperation?.configData,
					restoreListOperationsData
				},
				restoreListOperationsData: { ...restoreListOperationsData }
			};
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setResetListOperationsData = (
		listOperationId: ListOperationsIdentifierEnum,
		resetData: ListOperationsResetType,
		caller: string
	): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, resetData: { ...resetData } };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setSearch = ( listOperationId: ListOperationsIdentifierEnum, search: string[], caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, search };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setSearchIsInitialized = ( listOperationId: ListOperationsIdentifierEnum, searchIsInitialized: boolean, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, searchIsInitialized };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setSort = ( listOperationId: ListOperationsIdentifierEnum, sort: string, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, sort };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};

	private setSortIsInitialized = ( listOperationId: ListOperationsIdentifierEnum, sortIsInitialized: boolean, caller: string ): void => {
		const callback = ( listOperation: ListOperations ): ListOperations => {
			return { ...listOperation, sortIsInitialized };
		};
		this.updateListOperationProperty( listOperationId, callback, StoreActionsEnum.UPSERT_ONE, caller );
	};


	// SELECTORS ############################
	private allListOperations = ( caller: string ): Observable<ListOperations[]> => {
		return this.store$?.pipe( select<Record<string, ListOperations[]>, ListOperations[]>( this.listOperationsSelectors.selectAllListOperations ) );
	};

	private filterIsInitialized = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<boolean> => {
		return this.selectOnExistingValueAndChangeOnly<boolean>(
			select( this.listOperationsSelectors.selectListOperationsFilterIsInitialized( listOperationId ) ),
			caller
		);
	};

	private hideFilter = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<boolean> => {
		return this.selectOnExistingValueAndChangeOnly<boolean>(
			select( this.listOperationsSelectors.selectListOperationsHideFilter( listOperationId ) ),
			caller
		);
	};

	private hideSearch = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<boolean> => {
		return this.selectOnExistingValueAndChangeOnly<boolean>(
			select( this.listOperationsSelectors.selectListOperationsHideSearch( listOperationId ) ),
			caller
		);
	};

	private hideSort = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<boolean> => {
		return this.selectOnExistingValueAndChangeOnly<boolean>(
			select( this.listOperationsSelectors.selectListOperationsHideSort( listOperationId ) ),
			caller
		);
	};

	private listOperationsById = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<ListOperations> => {
		return this.selectOnExistingValueAndChangeOnly<ListOperations>(
			select( this.listOperationsSelectors.selectListOperationsById( listOperationId ) ),
			caller
		);
	};

	private paging = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<IPaging> => {
		return this.selectOnExistingValueAndChangeOnly<IPaging>(
			select( this.listOperationsSelectors.selectListOperationsPaging( listOperationId ) ),
			caller
		);
	};

	private pagingIsInitialized = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<boolean> => {
		return this.selectOnExistingValueAndChangeOnly<boolean>(
			select( this.listOperationsSelectors.selectListOperationsPagingIsInitialized( listOperationId ) ),
			caller
		);
	};

	private renderFilter = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<boolean> => {
		return this.selectOnExistingValueAndChangeOnly<boolean>(
			select( this.listOperationsSelectors.selectListOperationsRenderFilter( listOperationId ) ),
			caller
		);
	};

	private renderSearch = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<boolean> => {
		return this.selectOnExistingValueAndChangeOnly<boolean>(
			select( this.listOperationsSelectors.selectListOperationsRenderSearch( listOperationId ) ),
			caller
		);
	};

	private renderSort = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<boolean> => {
		return this.selectOnExistingValueAndChangeOnly<boolean>(
			select( this.listOperationsSelectors.selectListOperationsRenderSort( listOperationId ) ),
			caller
		);
	};

	private search = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<string[]> => {
		return this.selectOnExistingValueAndChangeOnly<string[]>(
			select( this.listOperationsSelectors.selectListOperationsSearch( listOperationId ) ),
			caller
		);
	};

	private searchIsInitialized = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<boolean> => {
		return this.selectOnExistingValueAndChangeOnly<boolean>(
			select( this.listOperationsSelectors.selectListOperationsSearchIsInitialized( listOperationId ) ),
			caller
		);
	};

	private sort = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<string> => {
		return this.selectOnExistingValueAndChangeOnly<string>(
			select( this.listOperationsSelectors.selectListOperationsSort( listOperationId ) ),
			caller
		);
	};

	private sortIsInitialized = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<boolean> => {
		return this.selectOnExistingValueAndChangeOnly<boolean>(
			select( this.listOperationsSelectors.selectListOperationsSortIsInitialized( listOperationId ) ),
			caller
		);
	};

	private activeFilters = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<IActiveFilters> => {
		return this.selectOnExistingValueAndChangeOnly<IActiveFilters>(
			select( this.listOperationsSelectors.selectListOperationsActiveFilters( listOperationId ) ),
			caller
		);
	};

	private restoreListOperationsData = (
		listOperationId: ListOperationsIdentifierEnum,
		caller: string,
		ignoreUndefined?: boolean
	): Observable<IRestoreListOperationsData> => {
		return this.selectOnExistingValueAndChangeOnly<IRestoreListOperationsData>(
			select( this.listOperationsSelectors.selectListOperationsRestoreListOperationsData( listOperationId ) ),
			caller,
			ignoreUndefined
		);
	};

	private resetListOperationsData = (
		listOperationId: ListOperationsIdentifierEnum,
		caller: string
	): Observable<ListOperationsResetType> => {
		return this.selectOnExistingValueAndChangeOnly<ListOperationsResetType>(
			select( this.listOperationsSelectors.selectListOperationsResetData( listOperationId ) ),
			caller,
			true,
			true
		);
	};

	private configData = ( listOperationId: ListOperationsIdentifierEnum, caller: string ): Observable<IListOperationsConfig> => {
		return this.selectOnExistingValueAndChangeOnly<IListOperationsConfig>(
			select( this.listOperationsSelectors.selectListOperationsConfigData( listOperationId ) ),
			caller,
			true
		);
	};

	private activeListOperationsId = ( caller: string ): Observable<ListOperationsIdentifierEnum> => {
		return this.selectOnExistingValueAndChangeOnly<ListOperationsIdentifierEnum>(
			select( this.listOperationsSelectors.selectActiveListOperationsId ),
			caller
		);
	};


	// HELPERS ##############################
	private filterResetDataByResetMode(
		listOperationId: ListOperationsIdentifierEnum,
		filterId: number,
		resetMode: ListOperationsResetModeEnum
	): FilterType {

		let resetData: FilterType;

		this.select.configData(
			listOperationId,
			'ListOperationsService:filterResetDataByResetMode'
		)
		.pipe(
			take( 1 )
		)
		.subscribe( ( configData: IListOperationsConfig ) => {

			// TODO keeps old listOps backwards compatibility, remove fallback when switching completely to new ListOps
			let filterData: FilterType[];

			// gets filterData by resetMode
			switch ( resetMode ) {
				case ListOperationsResetModeEnum.DEFAULT:
					// TODO keeps old listOps backwards compatibility, remove fallback when switching completely to new ListOps
					filterData = configData?.defaultData?.filterData ?? configData.filterData;
					resetData = filterData.find( ( filter: FilterType ) => filter.id === filterId );
					break;

				case ListOperationsResetModeEnum.RESTORE:
					// TODO keeps old listOps backwards compatibility, remove fallback when switching completely to new ListOps
					filterData = configData?.restoreData?.filterData ?? configData.filterData;
					resetData = filterData.find( ( filter: FilterType ) => filter.id === filterId );
					break;
			}
		} );

		return resetData;
	}

	/**
	 * Allows calling a "GET list" endpoint of the REST API with the current
	 * ListOps state (i.e. the latest settings of the list operators on the
	 * corresponding list page or tab etc.) in the NgRx store.
	 * @param apiReqFn An anonymous function that calls the `<Entity>ApiService`
	 * method meant to be called with the currently NgRx-stored ListOps data as
	 * request parameters.
	 * @param args An array that holds the arguments of the calling function as
	 * they are meant to be passed to the `apiReqFn` (which will pass them on to
	 * the actual `<Entity>ApiService` method). Hence, the order of the arguments
	 * in the array has to match the order of the arguments in the parameter list
	 * of the `apiReqFn`, but without the actual ListOps data.
	 * @param indexForListOperationsInArgsArray The index at which the ListOps
	 * data from the NgRx store have to be inserted **as request parameters** into
	 * the `args` array before the `apiReqFn` is called. Note that the position
	 * has to match the position of the corresponding argument in the parameter
	 * list of the `apiReqFn`.<br>
	 * @param listOperationsId The identifier of the ListOps state to be selected
	 * from the NgRx store (i.e. the identifier of the current ListOps state to be
	 * used as parameters for the request).
	 * Example:
	 * * caller: `XyzService.getList(someArg, listOpsId, anotherArg)`
	 * * apiReqFn: `(someArg, anotherArg, listOpsData) => XyzApiService.getList(someArg, anotherArg, listOpsData)`
	 * * args: `[someArg, anotherArg]`
	 * * indexForListOperationsInArgsArray: `2`
	 *
	 */
	private requestWithStoredListOperationsData = <EntityType, RequestParamsType>(
		apiReqFn: ( ...args: any ) => Observable<RequestCompleteListState<EntityType>>,
		args: any[],
		indexForListOperationsInArgsArray: number,
		listOperationsId: ListOperationsIdentifierEnum
	): Observable<RequestCompleteListState<EntityType>> => {
		return this.listOperationsById( listOperationsId, '' )
		.pipe(
			take( 1 ),
			switchMap( ( listOperations: ListOperations ) => {

				const listOperationsHttpParams: IListOperationsHttpParams = {
					activeFilters: listOperations.activeFilters,
					paging: listOperations.paging,
					search: listOperations.search,
					sort: listOperations.sort
				};

				args = ArrayUtils.insertAtIndex(
					args,
					[ listOperationsHttpParams as RequestParamsType ],
					indexForListOperationsInArgsArray,
					{ appendOnOutOfBounds: true }
				);
				return args?.length
							 ? apiReqFn( args )
							 : apiReqFn();
			} ),
			tap( ( requestState: RequestCompleteListState<EntityType> ) => {
				this.setPaging(
					listOperationsId,
					{ ...requestState.paging },
					'requestWithStoredListOperationsData:tap->response'
				);
			} )
		);
	};

	private sanitizeListOpsConfigToUserPermissions = ( listOperationsConfigData: IListOperationsConfig ): IListOperationsConfig => {

		if ( listOperationsConfigData?.filterData ) {
			const filterData: IFilter[] =
				listOperationsConfigData.filterData?.filter( ( filterItem: IFilter ) => {

					if ( filterItem.permissionConfig ) {

						if ( filterItem.permissionConfig.revokeScopeOnManagedObjectTypes?.length ) {
							const manageObject: ManagedObject = this.currentUserService.getManagedObject();
							const manageObjectType: string = manageObject?.managedObjectType?.toLowerCase();
							const manageObjectTypeList: string[] =
								filterItem.permissionConfig.revokeScopeOnManagedObjectTypes
								.map( ( managedObjectType: string ) => managedObjectType?.toLowerCase() );

							if ( manageObjectTypeList.includes( manageObjectType ) ) {
								return false;
							}
						}

						const scope: ApplicationScopeTypes = filterItem.permissionConfig.requiredScope;
						if ( scope ) {
							return this.authorizationService.hasPermission( scope );
						}
					}

					return true;
				} );

			return {
				...listOperationsConfigData,
				filterData
			};
		}

		return listOperationsConfigData;
	};
}
